import type { ResultReceiveView } from "./result-retention";

/** 결과 받기 기한이 **셀러에게 무엇으로 보이는가**. 서버 거절 코드 · 서버 판정을 읽는 함수 · 셀러 문장.
 *
 * ⚠️ **`result-retention.ts` 와 짝이다. 합치지 마라.** 그 파일은 기한을 **셈하는** 정본이고
 * `apps/api` 의 차단 경로와 파기 크론이 읽는다. 이 파일은 서버가 셈한 **결과**(작업 보기의
 * `resultReceive`, 또는 409)를 받은 뒤 화면이 쓰는 것만 둔다. 화면은 여기서 기한을 셈하지 않는다.
 * 같은 규율로 `precut.ts`(판정) · `precut-sheet.ts`(문구)가 갈려 있다(`docs/DATA_DICTIONARY.md` 50장).
 *
 * ⛔ **D1 에 아무 칸도 두지 않는다.** 이 파일은 마이그레이션도 DTO 도 바꾸지 않는다. 읽는 칸
 * `DetailJobView.resultReceive` 는 `docs/DATA_DICTIONARY.md` 45장(#218)이 세웠다.
 *
 * 글자 정본은 `design-manager`(PRD `7.6.14c` · `7.6.23a`)다. 화면에서 고쳐 쓰지 않는다. */

// ───────────────────────────────────────────────────────────────────────────
// 셀러에게 하는 말 (PRD `7.6.14c`. 2026-10-13 `design-manager` 확정)
// ───────────────────────────────────────────────────────────────────────────
//
// ⭐ **이 문장들은 화면이 기한을 셈해서 쓰는 것이 아니다.** 서버가 「기한이 지났다」 또는
// 「지금 만들면 기한 안에 못 받는다」고 판정해 준 뒤에만 쓴다. 판정은 두 길로 온다. 작업 보기의
// `resultReceive.state === "CLOSED"`(처음부터. `isResultReceiveClosed`)와 받기 · 만들기 경로의
// 409(누른 뒤. `isResultReceiveExpired`). 작업 보기는 기한 시각 `until` 도 싣지만 **날짜를 적는
// 문장은 아직 없다.** 화면에 날짜를 보일지는 `design-manager` 판단이고, 그 전까지 문장은 규칙
// (결제한 날부터 1개월)만 말한다. 날짜를 그리게 되면 `until` 을 자르지 말고
// `resultReceiveLastKstDay(until)` 을 쓴다(`docs/DATA_DICTIONARY.md` 45.4 화면 4).
//
// ⛔ **결과 주소가 `null` 인 것만으로 이 문장을 띄우지 마라.** 서버는 기한을 판정하지 못했을
// 때(`resultReceive.state === "UNKNOWN"`)도 닫는 쪽으로 떨어져 주소를 비운다
// (`apps/api/src/routes/jobs.ts` 의 `loadJobView`). 그 상태에서 「1개월이 지났습니다」를 쓰면 일시
// 장애를 기한 지남으로 말하게 된다. 처음부터 띄울 때는 `isResultReceiveClosed` 하나만 본다.
//
// 쓰지 않는 말(`7.6.14c` 끝): `만료` · `기한` · `기간 만료` · `보관 종료` · `지웠습니다` · `한 달` ·
// 결과에 붙인 `30일` · `결제일부터` · `다운로드` · `내려받기`.

/** 받기 경로(내보내기 · 확정 · 판 파일 · 묶음 · 장 결과)와 만들기 경로(만들기 · 다시 만들기 ·
 *  되살리기)가 기한 때문에 거절할 때 서버가 싣는 코드. `apps/api/src/lib/result-access.ts` 의
 *  `RESULT_RECEIVE_EXPIRED` 와 같은 글자이고 아직 두 벌이다(한 벌로 합치는 것은
 *  `docs/DATA_DICTIONARY.md` 50.2 후속). */
export const RESULT_RECEIVE_EXPIRED_CODE = "RESULT_RECEIVE_EXPIRED";

/** 받기 기간이 지난 작업을 말하는 문장 한 벌. 앱은 줄바꿈, 웹은 띄어쓰기로 잇고 글자는 같다. */
export const RESULT_RECEIVE_EXPIRED_COPY = {
  /** `!` 배너 첫 줄. */
  head: "이 작업의 결과는 이제 받을 수 없습니다.",
  /** `!` 배너 둘째 줄. */
  reason: "결제한 날부터 1개월이 지났습니다.",
  /** 받기 버튼(`다시 받기` · `전체 받기` · `받기` · `공유` · 확정)을 끌 때 버튼 아래 사유. */
  disabledReason: "결제한 날부터 1개월이 지나 누를 수 없습니다.",
  /** 받기 경로가 409 로 돌아왔을 때의 알림(토스트 · 힌트). 「다시 해 주세요」를 붙이지 않는다.
   *  기다려도 풀리지 않는다. */
  rejected: "결제한 날부터 1개월이 지나 이 작업의 결과는 이제 받을 수 없습니다.",
} as const;

/** 만들기 경로(다시 만들기 `A-25` · 되살리기 `A-25b` · 웹 `W-21`)가 409 로 돌아왔을 때.
 *
 * ⭐ **「지났습니다」를 쓰지 않는다.** 서버는 처리 임대(`CUTOUT_PROCESSING_LEASE_MS`)만큼 앞당겨
 * 거절하므로 기한 몇 분 전에도 같은 409 가 온다(`canMakeBeforeReceiveDeadline`). */
export const RESULT_MAKE_EXPIRED_COPY = {
  /** `A-31f` head 와 같은 글자다. */
  head: "이제 다시 만들어 드릴 수 없습니다.",
  reason: "결과는 결제한 날부터 1개월까지 받을 수 있어, 지금 만들면 받으실 수 없습니다.",
  /** **`A-25`(만든 장, ₩500) 에만** 붙는다. `A-25b` 는 금액 줄 `더 내실 금액 · 없음` 이 이미 같은
   *  사실을 말해 붙이지 않는다(판정 9. 같은 사실을 두 꼴로 말하지 않는다). 공급자를 부르기 전에
   *  거절했으므로 참이다. */
  money: "금액은 들지 않았습니다.",
  disabledReason: "지금 만들면 결과를 받으실 수 없어 누를 수 없습니다.",
} as const;

/** 되살리기가 잠금 뒤에 장을 대기로 돌리지 못했을 때 서버가 싣는 코드(PR #212). 대개 같은 장의
 *  동시 되살리기다. 공급자도 청구 행도 없다. */
export const IMAGE_NOT_REQUEUED_CODE = "IMAGE_NOT_REQUEUED";

/** `IMAGE_NOT_REQUEUED` 의 문장. ⛔ 「다른 기기에서 먼저」라고 단정하지 않는다(원인이 하나가
 *  아니다). 돈 문장도 붙이지 않는다(되살리기 금액 줄이 이미 말한다). 앱에는 「새로 고침」이 없어
 *  「닫고 확인」으로 쓴다. */
export const IMAGE_NOT_REQUEUED_TEXT =
  "이 장은 이미 다시 만들고 있거나 그사이 상태가 바뀌었습니다. 닫고 이 장의 지금 상태를 확인해 주세요.";

/** 받기 경로 실패가 **기한 지남**인가. 판정은 서버의 상태 코드와 `code` 둘뿐이다. 화면이 기한을
 *  셈하지 않는다. `RESULT_RECEIVE_CHECK_FAILED`(500, 판정 못 함)는 이 상태가 아니다. */
export function isResultReceiveExpired(error: { status: number; code?: string | null } | null | undefined): boolean {
  return !!error && error.status === 409 && error.code === RESULT_RECEIVE_EXPIRED_CODE;
}

/** 원본 사진 지우기 확인 창의 가운데 문장(PRD `7.6.14c` ⑤). 옛 「이미 내보낸 파일은 남지만」은
 *  결과에 기간이 없던 때의 말이라 2026-10-10 결정 뒤 틀렸다.
 *  - `ONE_JOB`: 한 작업의 창(웹 `W-50`).
 *  - `EACH_JOB`: 작업 전부에 걸린 창(앱 `my/data.tsx`). 기간을 작업마다 세므로 `작업마다` 가 붙는다. */
export const EXPORTED_FILES_AFTER_ORIGINAL_DELETE = {
  ONE_JOB: "이미 내보낸 파일은 결제한 날부터 1개월까지 다시 받을 수 있지만, 다시 만들 수는 없습니다.",
  EACH_JOB:
    "이미 내보낸 파일은 작업마다 결제한 날부터 1개월까지 다시 받을 수 있지만, 다시 만들 수는 없습니다.",
} as const;

/** 앱 탈퇴(`A-48b`) 「지워지는 것」 그룹 아래 두 줄(PRD `7.6.23a`). 옛 「언제 지우는지는 아직 정해지지
 *  않았습니다」는 처리방침 제3조가 두 값을 다 가진 뒤 거짓이 됐다. 배지를 달지 않는다. */
export const WITHDRAW_DELETION_NOTES = [
  "탈퇴하시면 그때 둘 다 지웁니다.",
  "탈퇴하지 않으셔도 작업마다 원본 사진은 올린 날부터 30일, 만든 상세페이지는 결제한 날부터 1개월이 지나면 지웁니다.",
] as const;

/** **서버가 이 작업의 받기 기간이 지났다고 판정했는가**(`DetailJobView.resultReceive`, #218.
 * `docs/DATA_DICTIONARY.md` 45장 · 50.1).
 *
 * `CLOSED` 일 때만 참이다. 화면은 이 값이 참이면 409 를 기다리지 않고 처음부터 기한 지남 상태로
 * 그린다(PRD `7.6.14c` ①).
 * - ⛔ `UNKNOWN`(판정 못 함)은 기한 지남이 아니다. 거짓을 낸다. 그때는 지금처럼 409 · 500 으로 가른다.
 * - `undefined`(칸이 없는 옛 서버)도 거짓이다. 409 를 받은 뒤의 동작이 그대로 남는다.
 * - ⛔ 결과 주소가 `null` 인 것으로 대신하지 않는다. 서버는 `UNKNOWN` 에서도 주소를 비운다.
 * - ⛔ `until` 과 `servedAt` 을 비교해 다시 판정하지 않는다. 판정은 서버가 한 번 했다. */
export function isResultReceiveClosed(
  view: { resultReceive?: ResultReceiveView } | null | undefined,
): boolean {
  return view?.resultReceive?.state === "CLOSED";
}
